<script setup lang="ts">
import HiddenValidation from "#/components/form/HiddenValidation.vue";

export interface Choice {
  value: string;
  title: string;
  description?: string;
  badgeTitle?: string;
  enabled: boolean;
}

const props = withDefaults(
  defineProps<{
    choices: Array<Choice>;
    required: boolean;
  }>(),
  {
    required: false,
  }
);
const value = defineModel<string>({ required: true });

const select = (choice: Choice) => {
  if (!choice.enabled) {
    return;
  }

  value.value = choice.value;
};
</script>

<template>
  <div
    v-for="choice in choices"
    :key="choice.value"
    class="radio-card"
    :class="{
      'radio-card--selected': value === choice.value,
      'radio-card--disabled': !choice.enabled,
    }"
    @click="select(choice)"
  >
    <div class="radio-card__left">
      <span
        class="radio-card__dot"
        :class="{ 'radio-card__dot--active': value === choice.value }"
      ></span>
      <div>
        <div class="radio-card__label">{{ choice.title }}</div>
        <div v-if="choice.description" class="radio-card__description">
          {{ choice.description }}
        </div>
      </div>
    </div>
    <span v-if="choice.badgeTitle" class="radio-card__badge">{{
      choice.badgeTitle
    }}</span>
    <hidden-validation
      v-if="required"
      :value="value"
      :required="required"
      class="radio-card__hidden_validation"
    />
  </div>
</template>

<style lang="scss" scoped>
.radio-card {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 5px 16px;
  border: 1px solid var(--card-border-color);
  border-radius: var(--card-border-radius);
  cursor: pointer;
  background: #fff;
  transition: border-color 0.15s;
  min-height: 50px;
  margin-bottom: 7px;

  &:last-child {
    margin-bottom: 0;
  }

  &:hover {
    border-color: #ff8c00;
  }

  .radio-card__left {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .radio-card__label {
    font-size: 15px;
    color: #1a1a1a;
  }

  .radio-card__description {
    font-size: 14px;
    color: #838383;
  }

  .radio-card__dot {
    width: 17px;
    height: 17px;
    box-sizing: border-box;
    border-radius: 50%;
    border: 1px solid #c5c5c5;
    flex-shrink: 0;
    position: relative;
    transition: border-color 0.15s;
  }

  .radio-card__badge {
    padding: 2px 8px;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 400;
    background: #00bfa5;
    color: #fff;
  }

  .radio-card__hidden_validation {
    position: absolute;
    bottom: 0;
  }

  &--selected {
    border-color: #e0e0e0;

    .radio-card__dot {
      &::after {
        content: "";
        position: absolute;
        top: 3px;
        left: 3px;
        width: 9px;
        height: 9px;
        border-radius: 50%;
        background: #ff8c00;
      }
    }
  }

  &--disabled {
    border-color: var(--card-border-color);

    &:hover {
      border-color: var(--card-border-color);
    }

    .radio-card__label {
      color: var(--card-border-color);
    }

    .radio-card__description {
      color: var(--card-border-color);
    }

    .radio-card__dot {
      border-color: var(--card-border-color);

      &::after {
        background: var(--card-border-color);
      }
    }
  }
}
</style>
